import { useApolloClient } from '@vue/apollo-composable'
import { computed, type MaybeRefOrGetter, onBeforeUnmount, reactive, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import bus, { eventNames } from '@/lib/eventBus'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

import { type OnboardingImageKind, persistOnboardingImageFile } from '../lib/onboardingImageUpload'
import type { OnboardingWizardCompany } from '../lib/onboardingWizardCompany'

export type { OnboardingImageKind }

export function useOnboardingFormImages(options: {
  wizardCompany: MaybeRefOrGetter<OnboardingWizardCompany | null | undefined>
  onUploaded: (payload: { kind: OnboardingImageKind; url?: string }) => void
  /**
   * Locally-prefilled form URLs (formData.companyLogo/teamPicture/portraitImage). Used as the
   * lowest-priority display fallback so an AI candidate pick — which lands in formData via the
   * chat field-update path but is never written to Company.images — previews here like every
   * other "Von KI erfasst" field, instead of showing an empty slot.
   */
  formValues?: {
    logo: MaybeRefOrGetter<string | undefined>
    team: MaybeRefOrGetter<string | undefined>
    profile: MaybeRefOrGetter<string | undefined>
  }
}) {
  const { t } = useI18n()
  const { client } = useApolloClient()

  /** Blob URLs for immediate preview after upload (Apollo cache can lag behind `wizardCompany`). */
  const localImagePreview = reactive<Partial<Record<OnboardingImageKind, string>>>({})
  /** Per-kind in-flight state so concurrent uploads of different kinds don't clear each other. */
  const uploadingKinds = reactive<Record<OnboardingImageKind, boolean>>({
    logo: false,
    team: false,
    profile: false,
  })
  const isUploading = (kind: OnboardingImageKind) => uploadingKinds[kind]

  const logoDisplayUrl = computed(
    () =>
      localImagePreview.logo ||
      toValue(options.wizardCompany)?.images?.logo?.url ||
      toValue(options.formValues?.logo) ||
      ''
  )
  const teamDisplayUrl = computed(
    () =>
      localImagePreview.team ||
      toValue(options.wizardCompany)?.images?.team?.url ||
      toValue(options.formValues?.team) ||
      ''
  )
  const profileDisplayUrl = computed(
    () =>
      localImagePreview.profile ||
      toValue(options.wizardCompany)?.ambassador?.image?.url ||
      toValue(options.formValues?.profile) ||
      ''
  )

  function revokeLocalPreview(kind: OnboardingImageKind) {
    const u = localImagePreview[kind]
    if (u) {
      URL.revokeObjectURL(u)
      delete localImagePreview[kind]
    }
  }

  watch(
    () => toValue(options.wizardCompany)?.images?.logo?.url,
    (url) => {
      if (url) revokeLocalPreview('logo')
    }
  )
  watch(
    () => toValue(options.wizardCompany)?.images?.team?.url,
    (url) => {
      if (url) revokeLocalPreview('team')
    }
  )
  watch(
    () => toValue(options.wizardCompany)?.ambassador?.image?.url,
    (url) => {
      if (url) revokeLocalPreview('profile')
    }
  )

  onBeforeUnmount(() => {
    revokeLocalPreview('logo')
    revokeLocalPreview('team')
    revokeLocalPreview('profile')
  })

  const persistImage = async (kind: OnboardingImageKind, blob: Blob) => {
    const company = toValue(options.wizardCompany)
    const companyId = company?.id
    if (!companyId) return
    // Ignore a re-entrant upload for a kind whose mutation is still pending.
    if (uploadingKinds[kind]) return

    const file = new File([blob], 'upload.jpg', { type: blob.type || 'image/jpeg' })

    uploadingKinds[kind] = true
    const objectUrl = URL.createObjectURL(file)
    revokeLocalPreview(kind)
    localImagePreview[kind] = objectUrl
    try {
      const uploadedUrl = await persistOnboardingImageFile(
        {
          company,
          mutate: (opts) => client.mutate(opts),
          query: (opts) => client.query(opts),
          extraRefetchQueries: [
            { query: PENDING_CHANGES_FOR_REGENERATION, variables: { companyId } },
          ],
        },
        { kind, file }
      )

      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'success',
        text: t('alerts.settings.company.success'),
      })
      options.onUploaded({ kind, url: uploadedUrl })
    } catch (e) {
      revokeLocalPreview(kind)
      console.error('[Onboarding] image upload failed', e)
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'error',
        text: t('alerts.settings.company.error'),
      })
    } finally {
      uploadingKinds[kind] = false
    }
  }

  return {
    logoDisplayUrl,
    teamDisplayUrl,
    profileDisplayUrl,
    isUploading,
    persistImage,
  }
}
